import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Paper,
  Tabs,
  Tab,
  Button,
  IconButton,
  Chip,
  Grid,
  Card,
  CardContent,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Alert,
  CircularProgress,
  Snackbar,
} from '@mui/material';
import {
  Edit,
  Delete,
  Add,
  Store as StoreIcon,
  People,
  Description,
  LocationOn,
  Upload,
} from '@mui/icons-material';
import ProfileAvatar from '../../components/common/ProfileAvatar';
import { useParams, useNavigate } from 'react-router-dom';
import { useForm, Controller } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import * as yup from 'yup';
import storeService from '../../services/storeService';
import clusterService from '../../services/clusterService';
import userService from '../../services/userService';
import storeLicenseService from '../../services/storeLicenseService';
import { Store, Cluster, User, StoreLicense } from '../../types';

interface TabPanelProps {
  children?: React.ReactNode;
  index: number;
  value: number;
}

function TabPanel(props: TabPanelProps) {
  const { children, value, index, ...other } = props;

  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`store-tabpanel-${index}`}
      aria-labelledby={`store-tab-${index}`}
      {...other}
    >
      {value === index && (
        <Box sx={{ p: 3 }}>
          {children}
        </Box>
      )}
    </div>
  );
}

const StoreDetailsPage: React.FC = () => {
  const { storeId } = useParams<{ storeId: string }>();
  const navigate = useNavigate();
  const [store, setStore] = useState<Store | null>(null);
  const [clusters, setClusters] = useState<Cluster[]>([]);
  const [storeManagers, setStoreManagers] = useState<User[]>([]);
  const [storeStaff, setStoreStaff] = useState<User[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [tabValue, setTabValue] = useState(0);
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [addUserDialogOpen, setAddUserDialogOpen] = useState(false);
  const [importDialogOpen, setImportDialogOpen] = useState(false);
  const [importFile, setImportFile] = useState<File | null>(null);
  const [importing, setImporting] = useState(false);
  
  // License state
  const [licenses, setLicenses] = useState<StoreLicense[]>([]);
  const [loadingLicenses, setLoadingLicenses] = useState(false);
  const [licenseDialogOpen, setLicenseDialogOpen] = useState(false);
  const [editingLicense, setEditingLicense] = useState<StoreLicense | null>(null);
  const [deletingLicense, setDeletingLicense] = useState<StoreLicense | null>(null);
  const [licenseFiles, setLicenseFiles] = useState<File[]>([]);
  const [viewingLicense, setViewingLicense] = useState<StoreLicense | null>(null);
  
  // Snackbar state
  const [snackbar, setSnackbar] = useState({
    open: false,
    message: '',
    severity: 'error' as 'success' | 'error' | 'warning' | 'info',
  });

  const {
    register,
    handleSubmit,
    reset,
    control,
    formState: { errors },
  } = useForm<Partial<Store>>({
    resolver: yupResolver(yup.object({
      name: yup.string().required('Store name is required'),
      code: yup.string().required('Store code is required'),
      city: yup.string().optional(),
      state: yup.string().optional(),
      description: yup.string().optional(),
      cluster_id: yup.string().required('Cluster is required'),
      manager_id: yup.string().optional().nullable(),
    })) as any,
  });

  // User form for adding staff
  const {
    register: registerUser,
    handleSubmit: handleSubmitUser,
    reset: resetUser,
    formState: { errors: userErrors },
  } = useForm({
    resolver: yupResolver(yup.object({
      first_name: yup.string().required('First name is required'),
      last_name: yup.string().required('Last name is required'),
      email: yup.string().email('Invalid email').required('Email is required'),
      phone: yup.string().required('Phone number is required'),
      role: yup.string().required('Role is required'),
      joining_date: yup.string().required('Joining date is required'),
    })) as any,
  });

  // License form
  const licenseSchema = yup.object({
    license_name: yup.string().required('License name is required'),
    description: yup.string().optional(),
    license_type: yup.string().optional(),
    issue_date: yup.string().optional(),
    expiry_date: yup.string().optional(),
    license_number: yup.string().optional(),
    issued_by: yup.string().optional(),
  });

  const {
    register: registerLicense,
    handleSubmit: handleSubmitLicense,
    reset: resetLicense,
    formState: { errors: licenseErrors },
  } = useForm({
    resolver: yupResolver(licenseSchema) as any,
  });

  // Helper functions for snackbar
  const showSnackbar = (message: string, severity: 'success' | 'error' | 'warning' | 'info' = 'error') => {
    setSnackbar({
      open: true,
      message,
      severity,
    });
  };

  const handleCloseSnackbar = () => {
    setSnackbar(prev => ({ ...prev, open: false }));
  };

  const fetchStoreDetails = async () => {
    if (!storeId) return;
    
    try {
      setLoading(true);
      const storeData = await storeService.getStoreById(storeId);
      setStore(storeData);
      reset(storeData);
    } catch (err: any) {
      setError(err.response?.data?.message || 'Failed to fetch store details');
    } finally {
      setLoading(false);
    }
  };

  const fetchClusters = async () => {
    try {
      const response = await clusterService.getClusters();
      setClusters(response.clusters);
    } catch (err: any) {
      console.error('Failed to fetch clusters:', err);
    }
  };

  const fetchStoreManagers = async () => {
    try {
      const response = await userService.getUsers({ role: 'store_manager' });
      setStoreManagers(response.users);
    } catch (err: any) {
      console.error('Failed to fetch store managers:', err);
    }
  };

  const fetchStoreStaff = async () => {
    if (!storeId) return;
    
    try {
      // Clear cache to ensure fresh data
      userService.clearCache();
      // Fetch users assigned to this store (only active users)
      const response = await userService.getUsers({ 
        store_id: storeId,
        is_active: true 
      });
      setStoreStaff(response.users || []);
    } catch (err: any) {
      console.error('Failed to fetch store staff:', err);
      setStoreStaff([]);
    }
  };

  useEffect(() => {
    const loadData = async () => {
      // Stagger API calls to prevent rate limiting
      await fetchStoreDetails();
      await new Promise(resolve => setTimeout(resolve, 200));
      await fetchClusters();
      await new Promise(resolve => setTimeout(resolve, 200));
      await fetchStoreManagers();
      await new Promise(resolve => setTimeout(resolve, 200));
      await fetchStoreStaff(); // Fetch staff on initial load
    };
    loadData();
  }, [storeId]);

  useEffect(() => {
    if (tabValue === 1) { // Staff tab
      fetchStoreStaff();
    } else if (tabValue === 3) { // Licenses tab
      fetchLicenses();
    }
  }, [tabValue, storeId]);

  // Reset form with store data when edit dialog opens
  useEffect(() => {
    if (editDialogOpen && store) {
      reset({
        name: store.name,
        code: store.code,
        cluster_id: store.cluster_id,
        city: store.city || '',
        state: store.state || '',
        description: store.description || '',
        manager_id: store.manager_id || '',
      });
    }
  }, [editDialogOpen, store, reset]);

  const handleTabChange = (event: React.SyntheticEvent, newValue: number) => {
    setTabValue(newValue);
  };

  const handleEditStore = async (data: Partial<Store>) => {
    if (!storeId) return;
    
    try {
      // Remove read-only fields before sending
      const { id, createdAt, updatedAt, created_by, updated_by, cluster, manager, ...updateData } = data as any;
      const updatedStore = await storeService.updateStore(storeId, updateData);
      setStore(updatedStore);
      setEditDialogOpen(false);
      showSnackbar('Store updated successfully!', 'success');
    } catch (err: any) {
      showSnackbar(err.response?.data?.message || 'Failed to update store', 'error');
    }
  };

  const handleEditStaff = (staff: User) => {
    // Navigate to users page with pre-filled data for this staff member
    window.location.href = `/users?edit=${staff.id}`;
  };

  // DISABLED: store deletion is not permitted for anyone - see the disabled
  // "Delete Store" button in the render tree below.
  // const handleDeleteStore = async () => {
  //   if (!storeId) return;
  //
  //   try {
  //     await storeService.deleteStore(storeId);
  //     showSnackbar('Store deleted successfully!', 'success');
  //     setTimeout(() => {
  //       navigate('/stores');
  //     }, 1500);
  //   } catch (err: any) {
  //     showSnackbar(err.response?.data?.message || 'Failed to delete store', 'error');
  //   }
  // };

  const handleFileUpload = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (file) {
      const fileType = file.name.split('.').pop()?.toLowerCase();
      if (fileType === 'csv' || fileType === 'xlsx' || fileType === 'xls') {
        setImportFile(file);
      } else {
        showSnackbar('Please select a valid CSV or Excel file', 'error');
      }
    }
  };

  const handleImportUsers = async () => {
    if (!importFile || !storeId) return;
    
    try {
      setImporting(true);
      const formData = new FormData();
      formData.append('file', importFile);
      formData.append('store_id', storeId);
      formData.append('cluster_id', store?.cluster_id || '');
      
      // This would need to be implemented in the backend
      // const response = await userService.importUsers(formData);
      
      showSnackbar('User import functionality will be implemented soon!', 'info');
      setImportDialogOpen(false);
      setImportFile(null);
    } catch (err: any) {
      showSnackbar(err.response?.data?.message || 'Failed to import users', 'error');
    } finally {
      setImporting(false);
    }
  };

  // License functions
  const fetchLicenses = async () => {
    if (!storeId) return;
    
    try {
      setLoadingLicenses(true);
      const licenseList = await storeLicenseService.getLicensesByStore(storeId);
      setLicenses(licenseList);
    } catch (err: any) {
      showSnackbar(err.response?.data?.message || 'Failed to fetch licenses', 'error');
    } finally {
      setLoadingLicenses(false);
    }
  };

  const handleCreateLicense = async (data: any) => {
    if (!storeId) return;
    
    try {
      if (licenseFiles.length === 0 && !editingLicense) {
        showSnackbar('Please upload at least one document', 'error');
        return;
      }

      const licenseData = {
        ...data,
        store_id: storeId,
      };

      if (editingLicense) {
        await storeLicenseService.updateLicense(editingLicense.id, licenseData);
        showSnackbar('License updated successfully!', 'success');
      } else {
        await storeLicenseService.createLicense(licenseData, licenseFiles);
        showSnackbar('License created successfully!', 'success');
      }

      setLicenseDialogOpen(false);
      resetLicense();
      setLicenseFiles([]);
      setEditingLicense(null);
      await fetchLicenses();
    } catch (err: any) {
      showSnackbar(err.response?.data?.message || 'Failed to save license', 'error');
    }
  };

  const handleDeleteLicense = async () => {
    if (!deletingLicense) return;
    
    try {
      await storeLicenseService.deleteLicense(deletingLicense.id);
      setLicenses(licenses.filter(l => l.id !== deletingLicense.id));
      setDeletingLicense(null);
      showSnackbar('License deleted successfully!', 'success');
    } catch (err: any) {
      showSnackbar(err.response?.data?.message || 'Failed to delete license', 'error');
      setDeletingLicense(null);
    }
  };

  const handleEditLicense = (license: StoreLicense) => {
    setEditingLicense(license);
    resetLicense({
      license_name: license.license_name,
      description: license.description || '',
      license_type: license.license_type || '',
      issue_date: license.issue_date || '',
      expiry_date: license.expiry_date || '',
      license_number: license.license_number || '',
      issued_by: license.issued_by || '',
    });
    setLicenseFiles([]);
    setLicenseDialogOpen(true);
  };

  const handleAddLicense = () => {
    setEditingLicense(null);
    resetLicense({
      license_name: '',
      description: '',
      license_type: '',
      issue_date: '',
      expiry_date: '',
      license_number: '',
      issued_by: '',
    });
    setLicenseFiles([]);
    setLicenseDialogOpen(true);
  };

  const handleFileChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const files = Array.from(event.target.files || []);
    setLicenseFiles([...licenseFiles, ...files]);
  };

  const removeFile = (index: number) => {
    setLicenseFiles(licenseFiles.filter((_, i) => i !== index));
  };

  const handleAddMoreDocuments = async (license: StoreLicense) => {
    if (licenseFiles.length === 0) {
      showSnackbar('Please select files to upload', 'error');
      return;
    }
    
    try {
      await storeLicenseService.addDocuments(license.id, licenseFiles);
      showSnackbar('Documents added successfully!', 'success');
      setLicenseFiles([]);
      await fetchLicenses();
    } catch (err: any) {
      showSnackbar(err.response?.data?.message || 'Failed to add documents', 'error');
    }
  };

  const onSubmit = async (data: any) => {
    if (!storeId) return;
    
    try {
      const userData = {
        ...data,
        store_id: storeId,
        cluster_id: store?.cluster_id,
      };
      
      const response = await userService.createUser(userData);
      showSnackbar(`Staff member ${response.user.first_name} ${response.user.last_name} added successfully!`, 'success');
      setAddUserDialogOpen(false);
      resetUser();
      
      // Refresh store staff list
      await fetchStoreStaff();
    } catch (err: any) {
      showSnackbar(err.response?.data?.message || 'Failed to add staff member', 'error');
    }
  };

  const getClusterName = (clusterId: string | undefined) => {
    if (!clusterId) return 'Unknown';
    const cluster = clusters.find(c => c.id === clusterId);
    return cluster ? cluster.name : 'Unknown';
  };

  const getManagerName = (managerId: string | undefined) => {
    if (!managerId) return 'Unknown';
    const manager = storeManagers.find(m => m.id === managerId);
    return manager ? `${manager.first_name} ${manager.last_name}` : 'Unknown';
  };

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
        <CircularProgress />
      </Box>
    );
  }

  if (error || !store) {
    return (
      <Box>
        <Alert severity="error" sx={{ mb: 2 }}>
          {error || 'Store not found'}
        </Alert>
        <Button onClick={() => navigate('/stores')} variant="contained">
          Back to Stores
        </Button>
      </Box>
    );
  }

  return (
    <Box>
      {/* Header */}
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={4}>
        <Box>
          <Typography variant="h4" component="h1" sx={{ color: '#081122', fontWeight: 600, mb: 1 }}>
            {store.name} Details
          </Typography>
          <Typography variant="body1" sx={{ color: '#666666' }}>
            Store Code: {store.code} | Cluster: {getClusterName(store.cluster_id)}
          </Typography>
        </Box>
        <Box sx={{ display: 'flex', gap: 2 }}>
          <Button
            variant="outlined"
            startIcon={<Edit />}
            onClick={() => setEditDialogOpen(true)}
            sx={{
              borderRadius: 2,
              px: 3,
              py: 1,
              borderColor: '#d0d0d0',
              color: '#666666',
              '&:hover': {
                borderColor: '#999999',
                backgroundColor: '#f5f5f5',
              }
            }}
          >
            Edit Store
          </Button>
          {/* DISABLED: Store deletion is not permitted for anyone.
              StoreService.deleteStore hard-deletes the store row and, with it, every
              attendance and leave record belonging to that store, while unassigning
              (store_id = null) every user in it. This is irreversible and has already
              caused data loss once (the TBJ8 "Hopefarm" store, orphaning 151 users).
              Do not re-enable without a soft-delete/archive path on the backend.
          <Button
            variant="outlined"
            startIcon={<Delete />}
            onClick={() => setDeleteDialogOpen(true)}
            sx={{
              borderRadius: 2,
              px: 3,
              py: 1,
              borderColor: '#f44336',
              color: '#f44336',
              '&:hover': {
                borderColor: '#d32f2f',
                backgroundColor: '#ffebee',
              }
            }}
          >
            Delete Store
          </Button>
          */}
        </Box>
      </Box>

      {/* Compact Store Summary */}
      <Card sx={{ mb: 3, borderRadius: 3, boxShadow: '0 4px 12px rgba(0, 0, 0, 0.1)' }}>
        <CardContent sx={{ p: 2 }}>
          <Grid container spacing={2} alignItems="center">
            <Grid item xs={12} sm={6} md={3}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                <Box sx={{
                  backgroundColor: '#081122',
                  color: 'white',
                  borderRadius: 2,
                  p: 1.5,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                }}>
                  <StoreIcon />
                </Box>
                <Box>
                  <Typography variant="h6" sx={{ fontWeight: 600, color: '#081122' }}>
                    {store.name}
                  </Typography>
                  <Typography variant="body2" sx={{ color: '#666666' }}>
                    {store.code}
                  </Typography>
                </Box>
              </Box>
            </Grid>
            
            <Grid item xs={12} sm={6} md={3}>
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <LocationOn sx={{ fontSize: 16, color: '#6c757d' }} />
                  <Typography variant="body2" sx={{ color: '#333333' }}>
                    {(store as any).city || 'N/A'}, {(store as any).state || 'N/A'}
                  </Typography>
                </Box>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <People sx={{ fontSize: 16, color: '#6c757d' }} />
                  <Typography variant="body2" sx={{ color: '#333333' }}>
                    Manager: {getManagerName(store.manager_id)}
                  </Typography>
                </Box>
              </Box>
            </Grid>

            <Grid item xs={12} sm={6} md={3}>
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <Description sx={{ fontSize: 16, color: '#6c757d' }} />
                  <Typography variant="body2" sx={{ color: '#333333' }}>
                    Cluster: {getClusterName(store.cluster_id)}
                  </Typography>
                </Box>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <People sx={{ fontSize: 16, color: '#6c757d' }} />
                  <Typography variant="body2" sx={{ color: '#333333' }}>
                    Staff: {storeStaff.length} members
                  </Typography>
                </Box>
              </Box>
            </Grid>

            <Grid item xs={12} sm={6} md={3}>
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1, alignItems: { xs: 'flex-start', md: 'flex-end' } }}>
                <Chip 
                  label={store.is_active ? 'Active' : 'Inactive'} 
                  color={store.is_active ? 'success' : 'error'} 
                  size="small" 
                />
                <Typography variant="caption" sx={{ color: '#666666' }}>
                  Created: {new Date(store.created_at).toLocaleDateString()}
                </Typography>
              </Box>
            </Grid>
          </Grid>
        </CardContent>
      </Card>

      {/* Tabs */}
      <Paper sx={{ borderRadius: 3, boxShadow: '0 4px 12px rgba(0, 0, 0, 0.1)' }}>
        <Tabs
          value={tabValue}
          onChange={handleTabChange}
          sx={{
            borderBottom: '1px solid #e0e0e0',
            '& .MuiTab-root': {
              textTransform: 'none',
              fontWeight: 500,
            },
            '& .Mui-selected': {
              color: '#081122',
            },
          }}
        >
          <Tab label="Store Information" />
          <Tab label="Store Staff" />
          <Tab label="Attendance Settings" />
          <Tab label="Store Licenses" />
        </Tabs>

        <TabPanel value={tabValue} index={0}>
          <Grid container spacing={3}>
            <Grid item xs={12} md={6}>
              <Typography variant="h6" sx={{ color: '#081122', fontWeight: 600, mb: 2 }}>
                Basic Information
              </Typography>
              <Box sx={{ mb: 2 }}>
                <Typography variant="body2" sx={{ color: '#666666', mb: 0.5 }}>
                  Store Name
                </Typography>
                <Typography variant="body1" sx={{ color: '#333333' }}>
                  {store.name}
                </Typography>
              </Box>
              <Box sx={{ mb: 2 }}>
                <Typography variant="body2" sx={{ color: '#666666', mb: 0.5 }}>
                  Store Code
                </Typography>
                <Typography variant="body1" sx={{ color: '#333333' }}>
                  {store.code}
                </Typography>
              </Box>
              <Box sx={{ mb: 2 }}>
                <Typography variant="body2" sx={{ color: '#666666', mb: 0.5 }}>
                  Cluster
                </Typography>
                <Typography variant="body1" sx={{ color: '#333333' }}>
                  {getClusterName(store.cluster_id)}
                </Typography>
              </Box>
              <Box sx={{ mb: 2 }}>
                <Typography variant="body2" sx={{ color: '#666666', mb: 0.5 }}>
                  Manager
                </Typography>
                <Typography variant="body1" sx={{ color: '#333333' }}>
                  {getManagerName(store.manager_id)}
                </Typography>
              </Box>
            </Grid>
            <Grid item xs={12} md={6}>
              <Typography variant="h6" sx={{ color: '#081122', fontWeight: 600, mb: 2 }}>
                Location Details
              </Typography>
              <Box sx={{ mb: 2 }}>
                <Typography variant="body2" sx={{ color: '#666666', mb: 0.5 }}>
                  City
                </Typography>
                <Typography variant="body1" sx={{ color: '#333333' }}>
                  {(store as any).city || 'N/A'}
                </Typography>
              </Box>
              <Box sx={{ mb: 2 }}>
                <Typography variant="body2" sx={{ color: '#666666', mb: 0.5 }}>
                  State
                </Typography>
                <Typography variant="body1" sx={{ color: '#333333' }}>
                  {(store as any).state || 'N/A'}
                </Typography>
              </Box>
              <Box sx={{ mb: 2 }}>
                <Typography variant="body2" sx={{ color: '#666666', mb: 0.5 }}>
                  Coordinates
                </Typography>
                <Typography variant="body1" sx={{ color: '#333333' }}>
                  {store.warehouse_latitude || 'N/A'}, {store.warehouse_longitude || 'N/A'}
                </Typography>
              </Box>
              {(store as any).description && (
                <Box sx={{ mb: 2 }}>
                  <Typography variant="body2" sx={{ color: '#666666', mb: 0.5 }}>
                    Description
                  </Typography>
                  <Typography variant="body1" sx={{ color: '#333333' }}>
                    {(store as any).description}
                  </Typography>
                </Box>
              )}
            </Grid>
          </Grid>
        </TabPanel>

        <TabPanel value={tabValue} index={1}>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
            <Typography variant="h6" sx={{ color: '#081122', fontWeight: 600 }}>
              Store Staff ({storeStaff.length})
            </Typography>
            <Box sx={{ display: 'flex', gap: 2 }}>
              <Button
                variant="outlined"
                startIcon={<Description />}
                onClick={() => setImportDialogOpen(true)}
                sx={{
                  borderColor: '#6c757d',
                  color: '#6c757d',
                  '&:hover': {
                    borderColor: '#081122',
                    backgroundColor: '#f5f5f5',
                  },
                }}
              >
                Import Users
              </Button>
              <Button
                variant="contained"
                startIcon={<Add />}
                onClick={() => setAddUserDialogOpen(true)}
                sx={{
                  background: 'linear-gradient(135deg, #081122 0%, #1a2a3a 100%)',
                  '&:hover': {
                    background: 'linear-gradient(135deg, #1a2a3a 0%, #081122 100%)',
                  },
                  boxShadow: '0 4px 12px rgba(8, 17, 34, 0.3)',
                }}
              >
                Add Staff
              </Button>
            </Box>
          </Box>
          
          <TableContainer>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell>Employee ID</TableCell>
                  <TableCell>Name</TableCell>
                  <TableCell>Role</TableCell>
                  <TableCell>Phone</TableCell>
                  <TableCell>Status</TableCell>
                  <TableCell>Actions</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {storeStaff.map((staff) => (
                  <TableRow key={staff.id}>
                    <TableCell>{staff.employee_id}</TableCell>
                    <TableCell>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                        <ProfileAvatar
                          imageUrl={staff.profile_image_url}
                          firstName={staff.first_name}
                          lastName={staff.last_name}
                          sx={{ width: 32, height: 32, bgcolor: '#081122' }}
                        />
                        <Typography variant="body2">
                          {staff.first_name} {staff.last_name}
                        </Typography>
                      </Box>
                    </TableCell>
                    <TableCell>
                      <Chip
                        label={staff.role?.replace('_', ' ').toUpperCase()}
                        color="primary"
                        size="small"
                        variant="outlined"
                      />
                    </TableCell>
                    <TableCell>{staff.phone}</TableCell>
                    <TableCell>
                      <Chip
                        label={staff.is_active ? 'Active' : 'Inactive'}
                        color={staff.is_active ? 'success' : 'default'}
                        size="small"
                      />
                    </TableCell>
                    <TableCell>
                      <IconButton 
                        size="small" 
                        color="primary"
                        onClick={() => handleEditStaff(staff)}
                        title="Edit Staff Member"
                      >
                        <Edit />
                      </IconButton>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        </TabPanel>

        <TabPanel value={tabValue} index={2}>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
            <Typography variant="h6" sx={{ color: '#081122', fontWeight: 600 }}>
              Attendance Settings
            </Typography>
            <Button
              variant="contained"
              startIcon={<Edit />}
              onClick={() => {/* TODO: Open attendance settings dialog */}}
              sx={{
                background: 'linear-gradient(135deg, #081122 0%, #1a2a3a 100%)',
                '&:hover': {
                  background: 'linear-gradient(135deg, #1a2a3a 0%, #081122 100%)',
                },
                boxShadow: '0 4px 12px rgba(8, 17, 34, 0.3)',
              }}
            >
              Edit Settings
            </Button>
          </Box>
          
          <Grid container spacing={3}>
            <Grid item xs={12} md={6}>
              <Card sx={{ borderRadius: 3, boxShadow: '0 4px 12px rgba(0, 0, 0, 0.1)' }}>
                <CardContent>
                  <Typography variant="h6" sx={{ color: '#081122', fontWeight: 600, mb: 2 }}>
                    Punch Settings
                  </Typography>
                  <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                      <Typography variant="body2">Allow punch from app</Typography>
                      <Chip 
                        label={(store as any).attendance_settings?.allow_punch_from_app ? 'Enabled' : 'Disabled'} 
                        color={(store as any).attendance_settings?.allow_punch_from_app ? 'success' : 'default'} 
                        size="small" 
                      />
                    </Box>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                      <Typography variant="body2">Selfie attendance</Typography>
                      <Chip 
                        label={(store as any).attendance_settings?.selfie_attendance ? 'Enabled' : 'Disabled'} 
                        color={(store as any).attendance_settings?.selfie_attendance ? 'success' : 'default'} 
                        size="small" 
                      />
                    </Box>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                      <Typography variant="body2">QR attendance</Typography>
                      <Chip 
                        label={(store as any).attendance_settings?.qr_attendance ? 'Enabled' : 'Disabled'} 
                        color={(store as any).attendance_settings?.qr_attendance ? 'success' : 'default'} 
                        size="small" 
                      />
                    </Box>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                      <Typography variant="body2">GPS attendance</Typography>
                      <Chip 
                        label={(store as any).attendance_settings?.gps_attendance ? 'Enabled' : 'Disabled'} 
                        color={(store as any).attendance_settings?.gps_attendance ? 'success' : 'default'} 
                        size="small" 
                      />
                    </Box>
                  </Box>
                </CardContent>
              </Card>
            </Grid>
            
            <Grid item xs={12} md={6}>
              <Card sx={{ borderRadius: 3, boxShadow: '0 4px 12px rgba(0, 0, 0, 0.1)' }}>
                <CardContent>
                  <Typography variant="h6" sx={{ color: '#081122', fontWeight: 600, mb: 2 }}>
                    Location & Time Settings
                  </Typography>
                  <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                      <Typography variant="body2">Mark from office only</Typography>
                      <Chip 
                        label={(store as any).attendance_settings?.mark_from_office_only ? 'Enabled' : 'Disabled'} 
                        color={(store as any).attendance_settings?.mark_from_office_only ? 'success' : 'default'} 
                        size="small" 
                      />
                    </Box>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                      <Typography variant="body2">Allowed radius</Typography>
                      <Typography variant="body2" sx={{ color: '#666666' }}>
                        {(store as any).attendance_settings?.allowed_radius || 100} meters
                      </Typography>
                    </Box>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                      <Typography variant="body2">Grace period</Typography>
                      <Typography variant="body2" sx={{ color: '#666666' }}>
                        {(store as any).attendance_settings?.grace_period_minutes || 15} minutes
                      </Typography>
                    </Box>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                      <Typography variant="body2">Max overtime hours</Typography>
                      <Typography variant="body2" sx={{ color: '#666666' }}>
                        {(store as any).attendance_settings?.max_overtime_hours || 4} hours
                      </Typography>
                    </Box>
                  </Box>
                </CardContent>
              </Card>
            </Grid>
          </Grid>
        </TabPanel>

        <TabPanel value={tabValue} index={3}>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
            <Typography variant="h6" sx={{ color: '#081122', fontWeight: 600 }}>
              Store Licenses
            </Typography>
            <Button
              variant="contained"
              startIcon={<Add />}
              onClick={handleAddLicense}
              sx={{
                background: 'linear-gradient(135deg, #081122 0%, #1a2a3a 100%)',
                '&:hover': {
                  background: 'linear-gradient(135deg, #1a2a3a 0%, #081122 100%)',
                },
                boxShadow: '0 4px 12px rgba(8, 17, 34, 0.3)',
              }}
            >
              Add License
            </Button>
          </Box>

          {loadingLicenses ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
              <CircularProgress />
            </Box>
          ) : licenses.length === 0 ? (
            <Alert severity="info">
              No licenses found. Click "Add License" to create a new license.
            </Alert>
          ) : (
            <TableContainer component={Paper} sx={{ borderRadius: 3, boxShadow: '0 4px 12px rgba(0, 0, 0, 0.1)' }}>
              <Table>
                <TableHead>
                  <TableRow sx={{ bgcolor: '#f5f5f5' }}>
                    <TableCell sx={{ fontWeight: 600 }}>License Name</TableCell>
                    <TableCell sx={{ fontWeight: 600 }}>Type</TableCell>
                    <TableCell sx={{ fontWeight: 600 }}>License Number</TableCell>
                    <TableCell sx={{ fontWeight: 600 }}>Issue Date</TableCell>
                    <TableCell sx={{ fontWeight: 600 }}>Expiry Date</TableCell>
                    <TableCell sx={{ fontWeight: 600 }}>Documents</TableCell>
                    <TableCell sx={{ fontWeight: 600 }}>Status</TableCell>
                    <TableCell sx={{ fontWeight: 600 }}>Actions</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {licenses.map((license) => {
                    const isExpired = license.expiry_date && new Date(license.expiry_date) < new Date();
                    const isExpiringSoon = license.expiry_date && 
                      new Date(license.expiry_date) > new Date() && 
                      new Date(license.expiry_date) < new Date(Date.now() + 30 * 24 * 60 * 60 * 1000);
                    
                    return (
                      <TableRow key={license.id} hover>
                        <TableCell>{license.license_name}</TableCell>
                        <TableCell>{license.license_type || 'N/A'}</TableCell>
                        <TableCell>{license.license_number || 'N/A'}</TableCell>
                        <TableCell>
                          {license.issue_date ? new Date(license.issue_date).toLocaleDateString() : 'N/A'}
                        </TableCell>
                        <TableCell>
                          {license.expiry_date ? (
                            <Box>
                              <Typography variant="body2">
                                {new Date(license.expiry_date).toLocaleDateString()}
                              </Typography>
                              {isExpired && (
                                <Chip label="Expired" color="error" size="small" sx={{ mt: 0.5 }} />
                              )}
                              {isExpiringSoon && !isExpired && (
                                <Chip label="Expiring Soon" color="warning" size="small" sx={{ mt: 0.5 }} />
                              )}
                            </Box>
                          ) : 'N/A'}
                        </TableCell>
                        <TableCell>
                          {license.documents && license.documents.length > 0 ? (
                            <Button
                              size="small"
                              onClick={() => setViewingLicense(license)}
                              startIcon={<Description />}
                            >
                              {license.documents.length} file{license.documents.length > 1 ? 's' : ''}
                            </Button>
                          ) : (
                            <Typography variant="body2" color="text.secondary">No documents</Typography>
                          )}
                        </TableCell>
                        <TableCell>
                          <Chip 
                            label={license.is_active ? 'Active' : 'Inactive'} 
                            color={license.is_active ? 'success' : 'default'} 
                            size="small" 
                          />
                        </TableCell>
                        <TableCell>
                          <IconButton size="small" onClick={() => handleEditLicense(license)}>
                            <Edit fontSize="small" />
                          </IconButton>
                          <IconButton 
                            size="small" 
                            onClick={() => setDeletingLicense(license)}
                            color="error"
                          >
                            <Delete fontSize="small" />
                          </IconButton>
                        </TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            </TableContainer>
          )}
        </TabPanel>
      </Paper>

      {/* Edit Store Dialog */}
      <Dialog 
        open={editDialogOpen} 
        onClose={() => setEditDialogOpen(false)} 
        maxWidth="md" 
        fullWidth
        PaperProps={{
          sx: {
            borderRadius: 3,
            boxShadow: '0 8px 32px rgba(0, 0, 0, 0.15)',
          }
        }}
      >
        <DialogTitle sx={{ 
          pb: 2, 
          borderBottom: '1px solid #e0e0e0',
          backgroundColor: '#f8f9fa'
        }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
            <Box sx={{
              width: 40,
              height: 40,
              borderRadius: 2,
              backgroundColor: '#2196f3',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              color: 'white'
            }}>
              <Edit />
            </Box>
            <Box>
              <Typography variant="h6" sx={{ color: '#081122', fontWeight: 600 }}>
                Edit Store
              </Typography>
              <Typography variant="body2" sx={{ color: '#666666' }}>
                Update store information
              </Typography>
            </Box>
          </Box>
        </DialogTitle>
        
        <form onSubmit={handleSubmit(handleEditStore)}>
          <DialogContent sx={{ p: 3 }}>
            <Grid container spacing={3}>
              <Grid item xs={12} sm={6}>
                <TextField
                  {...register('name')}
                  label="Store Name"
                  fullWidth
                  error={!!errors.name}
                  helperText={errors.name?.message}
                  sx={{
                    '& .MuiOutlinedInput-root': {
                      borderRadius: 2,
                    },
                  }}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  {...register('code')}
                  label="Store Code"
                  fullWidth
                  error={!!errors.code}
                  helperText={errors.code?.message}
                  sx={{
                    '& .MuiOutlinedInput-root': {
                      borderRadius: 2,
                    },
                  }}
                />
              </Grid>
              
              <Grid item xs={12}>
                <FormControl fullWidth error={!!errors.cluster_id}>
                  <InputLabel>Cluster</InputLabel>
                  <Controller
                    name="cluster_id"
                    control={control}
                    render={({ field }) => (
                  <Select
                        {...field}
                    label="Cluster"
                    sx={{
                      borderRadius: 2,
                    }}
                  >
                    {clusters.map((cluster) => (
                      <MenuItem key={cluster.id} value={cluster.id}>
                        {cluster.name} ({cluster.code})
                      </MenuItem>
                    ))}
                  </Select>
                    )}
                  />
                  {errors.cluster_id && (
                    <Typography variant="caption" color="error" sx={{ mt: 0.5, ml: 1.75 }}>
                      {errors.cluster_id.message}
                    </Typography>
                  )}
                </FormControl>
              </Grid>
              
              <Grid item xs={12} sm={6}>
                <TextField
                  {...register('city' as any)}
                  label="City"
                  fullWidth
                  error={!!(errors as any).city}
                  helperText={(errors as any).city?.message}
                  sx={{
                    '& .MuiOutlinedInput-root': {
                      borderRadius: 2,
                    },
                  }}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  {...register('state' as any)}
                  label="State"
                  fullWidth
                  error={!!(errors as any).state}
                  helperText={(errors as any).state?.message}
                  sx={{
                    '& .MuiOutlinedInput-root': {
                      borderRadius: 2,
                    },
                  }}
                />
              </Grid>
              
              <Grid item xs={12}>
                <TextField
                  {...register('description' as any)}
                  label="Description"
                  fullWidth
                  multiline
                  rows={3}
                  error={!!(errors as any).description}
                  helperText={(errors as any).description?.message}
                  sx={{
                    '& .MuiOutlinedInput-root': {
                      borderRadius: 2,
                    },
                  }}
                />
              </Grid>
              
              <Grid item xs={12}>
                <FormControl fullWidth error={!!errors.manager_id}>
                  <InputLabel>Store Manager (Optional)</InputLabel>
                  <Controller
                    name="manager_id"
                    control={control}
                    render={({ field }) => (
                  <Select
                        {...field}
                    label="Store Manager (Optional)"
                    sx={{
                      borderRadius: 2,
                    }}
                  >
                    <MenuItem value="">
                      <em>None - Unassign manager</em>
                    </MenuItem>
                    {storeManagers.map((manager) => (
                      <MenuItem key={manager.id} value={manager.id}>
                        {manager.first_name} {manager.last_name} ({manager.employee_id})
                      </MenuItem>
                    ))}
                  </Select>
                    )}
                  />
                  {errors.manager_id && (
                    <Typography variant="caption" color="error" sx={{ mt: 0.5, ml: 1.75 }}>
                      {errors.manager_id.message}
                    </Typography>
                  )}
                  <Typography variant="caption" sx={{ mt: 0.5, ml: 1.75, color: 'text.secondary' }}>
                    Store Manager can be assigned or changed later.
                  </Typography>
                </FormControl>
              </Grid>
            </Grid>
          </DialogContent>
          
          <DialogActions sx={{ 
            p: 3, 
            pt: 2, 
            borderTop: '1px solid #e0e0e0',
            backgroundColor: '#f8f9fa',
            gap: 2
          }}>
            <Button 
              onClick={() => setEditDialogOpen(false)}
              variant="outlined"
              sx={{ 
                borderRadius: 2,
                px: 3,
                py: 1,
                borderColor: '#d0d0d0',
                color: '#666666',
                '&:hover': {
                  borderColor: '#999999',
                  backgroundColor: '#f5f5f5',
                }
              }}
            >
              Cancel
            </Button>
            <Button 
              type="submit" 
              variant="contained"
              sx={{
                borderRadius: 2,
                px: 3,
                py: 1,
                background: 'linear-gradient(135deg, #081122 0%, #1a2a3a 100%)',
                '&:hover': {
                  background: 'linear-gradient(135deg, #1a2a3a 0%, #081122 100%)',
                },
                boxShadow: '0 4px 12px rgba(8, 17, 34, 0.3)',
              }}
            >
              Update Store
            </Button>
          </DialogActions>
        </form>
      </Dialog>

      {/* DISABLED: Delete Confirmation Dialog - see the disabled "Delete Store" button above.
      <Dialog
        open={deleteDialogOpen}
        onClose={() => setDeleteDialogOpen(false)}
        PaperProps={{
          sx: {
            borderRadius: 3,
            boxShadow: '0 8px 32px rgba(0, 0, 0, 0.15)',
          }
        }}
      >
        <DialogTitle sx={{ color: '#f44336', fontWeight: 600 }}>
          Delete Store
        </DialogTitle>
        <DialogContent>
          <Typography>
            Are you sure you want to delete "{store.name}"? This action cannot be undone.
          </Typography>
        </DialogContent>
        <DialogActions sx={{ gap: 2 }}>
          <Button
            onClick={() => setDeleteDialogOpen(false)}
            variant="outlined"
            sx={{
              borderRadius: 2,
              px: 3,
              py: 1,
            }}
          >
            Cancel
          </Button>
          <Button
            onClick={handleDeleteStore}
            variant="contained"
            color="error"
            sx={{
              borderRadius: 2,
              px: 3,
              py: 1,
            }}
          >
            Delete
          </Button>
        </DialogActions>
      </Dialog>
      */}

      {/* Import Users Dialog */}
      <Dialog
        open={importDialogOpen}
        onClose={() => setImportDialogOpen(false)}
        PaperProps={{
          sx: {
            borderRadius: 3,
            boxShadow: '0 8px 32px rgba(0, 0, 0, 0.15)',
            minWidth: 500,
          }
        }}
      >
        <DialogTitle sx={{ color: '#081122', fontWeight: 600 }}>
          Import Users from File
        </DialogTitle>
        <DialogContent sx={{ p: 3 }}>
          <Typography variant="body2" sx={{ color: '#666666', mb: 3 }}>
            Upload a CSV or Excel file to import multiple users to this store. The file should contain columns for user details.
          </Typography>
          
          <Box sx={{ mb: 3 }}>
            <input
              accept=".csv,.xlsx,.xls"
              style={{ display: 'none' }}
              id="import-file-input"
              type="file"
              onChange={handleFileUpload}
            />
            <label htmlFor="import-file-input">
              <Button
                variant="outlined"
                component="span"
                startIcon={<Upload />}
                sx={{
                  borderColor: '#6c757d',
                  color: '#6c757d',
                  '&:hover': {
                    borderColor: '#081122',
                    backgroundColor: '#f5f5f5',
                  },
                }}
              >
                Choose File
              </Button>
            </label>
            {importFile && (
              <Typography variant="body2" sx={{ mt: 1, color: '#333333' }}>
                Selected: {importFile.name}
              </Typography>
            )}
          </Box>

          <Alert severity="info" sx={{ mb: 2 }}>
            <Typography variant="body2">
              <strong>Expected columns:</strong> first_name, last_name, email, phone, role, joining_date
            </Typography>
          </Alert>
        </DialogContent>
        <DialogActions sx={{ gap: 2, p: 3 }}>
          <Button 
            onClick={() => {
              setImportDialogOpen(false);
              setImportFile(null);
            }}
            variant="outlined"
            sx={{ 
              borderRadius: 2,
              px: 3,
              py: 1,
            }}
          >
            Cancel
          </Button>
          <Button 
            onClick={handleImportUsers}
            variant="contained"
            disabled={!importFile || importing}
            sx={{
              borderRadius: 2,
              px: 3,
              py: 1,
              background: 'linear-gradient(135deg, #081122 0%, #1a2a3a 100%)',
              '&:hover': {
                background: 'linear-gradient(135deg, #1a2a3a 0%, #081122 100%)',
              },
            }}
          >
            {importing ? 'Importing...' : 'Import Users'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Add User Dialog */}
      <Dialog
        open={addUserDialogOpen}
        onClose={() => setAddUserDialogOpen(false)}
        PaperProps={{
          sx: {
            borderRadius: 3,
            boxShadow: '0 8px 32px rgba(0, 0, 0, 0.15)',
            minWidth: 600,
          }
        }}
      >
        <DialogTitle sx={{ color: '#081122', fontWeight: 600 }}>
          Add New Staff Member
        </DialogTitle>
        <DialogContent sx={{ p: 3 }}>
          <Typography variant="body2" sx={{ color: '#666666', mb: 3 }}>
            Add a new staff member to {store?.name}.
          </Typography>
          
          <Grid container spacing={2}>
            <Grid item xs={12} sm={6}>
              <TextField
                {...registerUser('first_name')}
                label="First Name"
                fullWidth
                variant="outlined"
                error={!!userErrors.first_name}
                helperText={userErrors.first_name?.message as string}
                sx={{ mb: 2 }}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                {...registerUser('last_name')}
                label="Last Name"
                fullWidth
                variant="outlined"
                error={!!userErrors.last_name}
                helperText={userErrors.last_name?.message as string}
                sx={{ mb: 2 }}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                {...registerUser('email')}
                label="Email"
                fullWidth
                variant="outlined"
                error={!!userErrors.email}
                helperText={userErrors.email?.message as string}
                sx={{ mb: 2 }}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                {...registerUser('phone')}
                label="Phone Number"
                fullWidth
                variant="outlined"
                error={!!userErrors.phone}
                helperText={userErrors.phone?.message as string}
                sx={{ mb: 2 }}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <FormControl fullWidth error={!!userErrors.role} sx={{ mb: 2 }}>
                <InputLabel>Role</InputLabel>
                <Select
                  {...registerUser('role')}
                  label="Role"
                  defaultValue="associate"
                >
                  <MenuItem value="associate">Associate</MenuItem>
                  <MenuItem value="shift_in_charge">Shift In Charge</MenuItem>
                  <MenuItem value="process_expert">Process Expert</MenuItem>
                  <MenuItem value="quality_manager">Quality Manager</MenuItem>
                  <MenuItem value="store_manager">Store Manager</MenuItem>
                </Select>
                {userErrors.role && (
                  <Typography variant="caption" color="error" sx={{ mt: 0.5, ml: 2 }}>
                    {userErrors.role?.message as string}
                  </Typography>
                )}
              </FormControl>
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                {...registerUser('joining_date')}
                label="Date of Joining"
                type="date"
                fullWidth
                variant="outlined"
                error={!!userErrors.joining_date}
                helperText={userErrors.joining_date?.message as string}
                InputLabelProps={{ shrink: true }}
                sx={{ mb: 2 }}
              />
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions sx={{ gap: 2, p: 3 }}>
          <Button 
            onClick={() => setAddUserDialogOpen(false)}
            variant="outlined"
            sx={{ 
              borderRadius: 2,
              px: 3,
              py: 1,
            }}
          >
            Cancel
          </Button>
          <Button 
            onClick={handleSubmitUser(onSubmit)}
            variant="contained"
            sx={{
              borderRadius: 2,
              px: 3,
              py: 1,
              background: 'linear-gradient(135deg, #081122 0%, #1a2a3a 100%)',
              '&:hover': {
                background: 'linear-gradient(135deg, #1a2a3a 0%, #081122 100%)',
              },
            }}
          >
            Add Staff Member
          </Button>
        </DialogActions>
      </Dialog>

      {/* License Create/Edit Dialog */}
      <Dialog
        open={licenseDialogOpen}
        onClose={() => {
          setLicenseDialogOpen(false);
          setEditingLicense(null);
          resetLicense();
          setLicenseFiles([]);
        }}
        PaperProps={{
          sx: {
            borderRadius: 3,
            boxShadow: '0 8px 32px rgba(0, 0, 0, 0.15)',
            minWidth: 600,
            maxWidth: 800,
          }
        }}
        maxWidth="md"
      >
        <DialogTitle sx={{ color: '#081122', fontWeight: 600 }}>
          {editingLicense ? 'Edit License' : 'Add New License'}
        </DialogTitle>
        <form onSubmit={handleSubmitLicense(handleCreateLicense)}>
          <DialogContent sx={{ p: 3 }}>
            <Grid container spacing={2}>
              <Grid item xs={12}>
                <TextField
                  {...registerLicense('license_name')}
                  label="License Name"
                  fullWidth
                  required
                  error={!!licenseErrors.license_name}
                  helperText={licenseErrors.license_name?.message as string}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  {...registerLicense('license_type')}
                  label="License Type"
                  fullWidth
                  placeholder="e.g., Food License, Trade License"
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  {...registerLicense('license_number')}
                  label="License Number"
                  fullWidth
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  {...registerLicense('issue_date')}
                  label="Issue Date"
                  type="date"
                  fullWidth
                  InputLabelProps={{ shrink: true }}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  {...registerLicense('expiry_date')}
                  label="Expiry Date"
                  type="date"
                  fullWidth
                  InputLabelProps={{ shrink: true }}
                />
              </Grid>
              <Grid item xs={12}>
                <TextField
                  {...registerLicense('issued_by')}
                  label="Issued By"
                  fullWidth
                  placeholder="Issuing authority"
                />
              </Grid>
              <Grid item xs={12}>
                <TextField
                  {...registerLicense('description')}
                  label="Description"
                  fullWidth
                  multiline
                  rows={3}
                />
              </Grid>
              {!editingLicense && (
                <Grid item xs={12}>
                  <Box>
                    <input
                      accept="image/*,.pdf,.doc,.docx"
                      style={{ display: 'none' }}
                      id="license-file-upload"
                      multiple
                      type="file"
                      onChange={handleFileChange}
                    />
                    <label htmlFor="license-file-upload">
                      <Button
                        variant="outlined"
                        component="span"
                        startIcon={<Upload />}
                        fullWidth
                        sx={{ py: 1.5 }}
                      >
                        Upload Documents
                      </Button>
                    </label>
                    {licenseFiles.length > 0 && (
                      <Box sx={{ mt: 2 }}>
                        {licenseFiles.map((file, index) => (
                          <Box
                            key={index}
                            sx={{
                              display: 'flex',
                              justifyContent: 'space-between',
                              alignItems: 'center',
                              p: 1,
                              mb: 1,
                              bgcolor: '#f5f5f5',
                              borderRadius: 1,
                            }}
                          >
                            <Typography variant="body2">{file.name}</Typography>
                            <IconButton size="small" onClick={() => removeFile(index)} color="error">
                              <Delete fontSize="small" />
                            </IconButton>
                          </Box>
                        ))}
                      </Box>
                    )}
                  </Box>
                </Grid>
              )}
            </Grid>
          </DialogContent>
          <DialogActions sx={{ p: 3, pt: 2 }}>
            <Button
              onClick={() => {
                setLicenseDialogOpen(false);
                setEditingLicense(null);
                resetLicense();
                setLicenseFiles([]);
              }}
              sx={{ borderRadius: 2, px: 3, py: 1 }}
            >
              Cancel
            </Button>
            <Button
              type="submit"
              variant="contained"
              sx={{
                borderRadius: 2,
                px: 3,
                py: 1,
                background: 'linear-gradient(135deg, #081122 0%, #1a2a3a 100%)',
                '&:hover': {
                  background: 'linear-gradient(135deg, #1a2a3a 0%, #081122 100%)',
                },
              }}
            >
              {editingLicense ? 'Update License' : 'Create License'}
            </Button>
          </DialogActions>
        </form>
      </Dialog>

      {/* View License Documents Dialog */}
      <Dialog
        open={!!viewingLicense}
        onClose={() => {
          setViewingLicense(null);
          setLicenseFiles([]);
        }}
        PaperProps={{
          sx: {
            borderRadius: 3,
            boxShadow: '0 8px 32px rgba(0, 0, 0, 0.15)',
            minWidth: 600,
            maxWidth: 800,
          }
        }}
        maxWidth="md"
      >
        <DialogTitle sx={{ color: '#081122', fontWeight: 600 }}>
          {viewingLicense?.license_name} - Documents
        </DialogTitle>
        <DialogContent sx={{ p: 3 }}>
          {viewingLicense && (
            <>
              <Box sx={{ mb: 3 }}>
                <Typography variant="body2" color="text.secondary" gutterBottom>
                  {viewingLicense.description || 'No description'}
                </Typography>
                <Box sx={{ mt: 2, display: 'flex', gap: 2, flexWrap: 'wrap' }}>
                  {viewingLicense.license_type && (
                    <Chip label={`Type: ${viewingLicense.license_type}`} size="small" />
                  )}
                  {viewingLicense.license_number && (
                    <Chip label={`Number: ${viewingLicense.license_number}`} size="small" />
                  )}
                </Box>
              </Box>

              <Typography variant="h6" sx={{ mb: 2, fontWeight: 600 }}>
                Existing Documents ({viewingLicense.documents?.length || 0})
              </Typography>

              {viewingLicense.documents && viewingLicense.documents.length > 0 ? (
                <Box sx={{ mb: 3 }}>
                  {viewingLicense.documents.map((doc) => (
                    <Box
                      key={doc.id}
                      sx={{
                        display: 'flex',
                        justifyContent: 'space-between',
                        alignItems: 'center',
                        p: 2,
                        mb: 1,
                        border: '1px solid #e0e0e0',
                        borderRadius: 1,
                      }}
                    >
                      <Box>
                        <Typography variant="body2" sx={{ fontWeight: 600 }}>
                          {doc.original_name}
                        </Typography>
                        <Typography variant="caption" color="text.secondary">
                          {(doc.file_size / 1024).toFixed(2)} KB
                        </Typography>
                      </Box>
                      <Box sx={{ display: 'flex', gap: 1 }}>
                        <Button
                          size="small"
                          href={doc.file_url}
                          target="_blank"
                          rel="noopener noreferrer"
                        >
                          View
                        </Button>
                        <IconButton
                          size="small"
                          color="error"
                          onClick={async () => {
                            if (window.confirm('Are you sure you want to delete this document?')) {
                              try {
                                await storeLicenseService.deleteDocument(doc.id);
                                showSnackbar('Document deleted successfully!', 'success');
                                await fetchLicenses();
                                const updated = licenses.find(l => l.id === viewingLicense.id);
                                if (updated) {
                                  const fullLicense = await storeLicenseService.getLicenseById(updated.id);
                                  setViewingLicense(fullLicense);
                                }
                              } catch (err: any) {
                                showSnackbar(err.response?.data?.message || 'Failed to delete document', 'error');
                              }
                            }
                          }}
                        >
                          <Delete fontSize="small" />
                        </IconButton>
                      </Box>
                    </Box>
                  ))}
                </Box>
              ) : (
                <Alert severity="info" sx={{ mb: 3 }}>
                  No documents uploaded yet.
                </Alert>
              )}

              <Box>
                <Typography variant="h6" sx={{ mb: 2, fontWeight: 600 }}>
                  Add More Documents
                </Typography>
                <input
                  accept="image/*,.pdf,.doc,.docx"
                  style={{ display: 'none' }}
                  id="add-license-documents"
                  multiple
                  type="file"
                  onChange={handleFileChange}
                />
                <label htmlFor="add-license-documents">
                  <Button
                    variant="outlined"
                    component="span"
                    startIcon={<Upload />}
                    fullWidth
                    sx={{ py: 1.5, mb: 2 }}
                  >
                    Select Files
                  </Button>
                </label>
                {licenseFiles.length > 0 && (
                  <Box sx={{ mb: 2 }}>
                    {licenseFiles.map((file, index) => (
                      <Box
                        key={index}
                        sx={{
                          display: 'flex',
                          justifyContent: 'space-between',
                          alignItems: 'center',
                          p: 1,
                          mb: 1,
                          bgcolor: '#f5f5f5',
                          borderRadius: 1,
                        }}
                      >
                        <Typography variant="body2">{file.name}</Typography>
                        <IconButton size="small" onClick={() => removeFile(index)} color="error">
                          <Delete fontSize="small" />
                        </IconButton>
                      </Box>
                    ))}
                    <Button
                      variant="contained"
                      onClick={() => {
                        if (viewingLicense) {
                          handleAddMoreDocuments(viewingLicense);
                          setViewingLicense(null);
                          setLicenseFiles([]);
                        }
                      }}
                      fullWidth
                      sx={{
                        mt: 2,
                        background: 'linear-gradient(135deg, #081122 0%, #1a2a3a 100%)',
                        '&:hover': {
                          background: 'linear-gradient(135deg, #1a2a3a 0%, #081122 100%)',
                        },
                      }}
                    >
                      Upload Files
                    </Button>
                  </Box>
                )}
              </Box>
            </>
          )}
        </DialogContent>
        <DialogActions sx={{ p: 3, pt: 2 }}>
          <Button
            onClick={() => {
              setViewingLicense(null);
              setLicenseFiles([]);
            }}
            sx={{ borderRadius: 2, px: 3, py: 1 }}
          >
            Close
          </Button>
        </DialogActions>
      </Dialog>

      {/* Delete License Confirmation Dialog */}
      <Dialog
        open={!!deletingLicense}
        onClose={() => setDeletingLicense(null)}
        PaperProps={{
          sx: {
            borderRadius: 3,
            boxShadow: '0 8px 32px rgba(0, 0, 0, 0.15)',
          }
        }}
      >
        <DialogTitle sx={{ color: '#081122', fontWeight: 600 }}>
          Delete License
        </DialogTitle>
        <DialogContent sx={{ p: 3 }}>
          <Typography variant="body1">
            Are you sure you want to delete the license "{deletingLicense?.license_name}"? 
            This action cannot be undone and all associated documents will be deleted.
          </Typography>
        </DialogContent>
        <DialogActions sx={{ p: 3, pt: 2 }}>
          <Button
            onClick={() => setDeletingLicense(null)}
            sx={{ borderRadius: 2, px: 3, py: 1 }}
          >
            Cancel
          </Button>
          <Button
            onClick={handleDeleteLicense}
            variant="contained"
            color="error"
            sx={{ borderRadius: 2, px: 3, py: 1 }}
          >
            Delete
          </Button>
        </DialogActions>
      </Dialog>

      {/* Snackbar for notifications */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={handleCloseSnackbar}
        anchorOrigin={{ vertical: 'top', horizontal: 'right' }}
      >
        <Alert 
          onClose={handleCloseSnackbar} 
          severity={snackbar.severity}
          sx={{ width: '100%' }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default StoreDetailsPage;
